import { tokenizedCardDataZodObject } from "@@/shared/payments/tokenized-cards/tokenized-card";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import type { _OrderEventBaseFactoryParams } from "../_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";
import { orderEventIdZodSchema } from "../_order-event-id";

/**
 * Order event for initiating a charge on a virtual card from an OTA.
 * This event signifies that even though the customer has already paid the OTA,
 * a virtual card is available and a subsequent charge will be performed.
 */
const orderEventVirtualCardRegisteredZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("PAYMENT_VIRTUAL_CARD_REGISTERED"),
        createdBy: userIdZodSchema.or(z.string()),
        amount: z.number().min(0),
        // The OTA name and ID that the virtual card is associated with.
        //Ota stands for Online Travel Agency (e.g. Booking.com, Expedia, etc.)
        otaName: z.string(),
        otaId: z.string(),
        // The virtual card details that will be charged.
        // If we don't have tokenized card data, the customer will be prompted to enter card details.
        virtualCard: tokenizedCardDataZodObject.optional(),
        // Reference to the event that this virtual card charge is related to.
        paymentFor: z.object({
            orderEventId: orderEventIdZodSchema,
        }),
    }),
);

type OrderPaymentVirtualCardRegisteredEvent = z.infer<
    typeof orderEventVirtualCardRegisteredZodObject
>;

const orderEventVirtualCardRegisteredZodSchema =
    orderEventVirtualCardRegisteredZodObject as z.ZodSchema<OrderPaymentVirtualCardRegisteredEvent>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderPaymentVirtualCardRegisteredEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;
type Params = Except<FactoryParams, "signature">;

const orderEventVirtualCardRegisteredFactory = (
    params: Params,
): OrderPaymentVirtualCardRegisteredEvent => ({
    ..._orderEventBaseFactory({
        ...params,
        signature: signatureFactory(
            params.orderId,
            "PAYMENT_VIRTUAL_CARD_REGISTERED",
            [params.paymentFor.orderEventId],
        ),
    }),
    type: "PAYMENT_VIRTUAL_CARD_REGISTERED",
    createdBy: params.createdBy,
    amount: params.amount,
    virtualCard: params.virtualCard,
    paymentFor: params.paymentFor,
    otaName: params.otaName,
    otaId: params.otaId,
});

const isOrderPaymentVirtualCardRegisteredEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is OrderPaymentVirtualCardRegisteredEvent => {
    return item.type === "PAYMENT_VIRTUAL_CARD_REGISTERED";
};

export {
    isOrderPaymentVirtualCardRegisteredEvent,
    orderEventVirtualCardRegisteredFactory,
    orderEventVirtualCardRegisteredZodObject,
    orderEventVirtualCardRegisteredZodSchema,
};
export type { OrderPaymentVirtualCardRegisteredEvent };
